import posthog from 'posthog-js';

export const THRESHOLDS = {
  EVENTS_PER_SECOND: 10,
  REPEATED_PAGEVIEWS: 10,
  EVENTS_PER_MINUTE: 100,
  EVENTS_AFTER_ANOMALY: 100,
} as const;

const STORAGE_KEYS = {
  IS_BOT: 'ph_bot',
  PAGEVIEWS: 'ph_pageviews',
  EVENT_COUNT: 'ph_event_count',
  EVENT_WINDOW_START: 'ph_event_window_start',
  TAP_COUNT: 'ph_tap_count',
  TAP_WINDOW_START: 'ph_tap_window_start',
} as const;

const isBrowser = () => typeof window !== 'undefined';

export const storage = {
  get: (key: string): string | null =>
    isBrowser() ? sessionStorage.getItem(key) : null,

  set: (key: string, value: string): void => {
    if (isBrowser()) sessionStorage.setItem(key, value);
  },
};

const state = {
  initialized: false,
  bot: false,
  taps: { count: 0, windowStart: 0 },
  events: { count: 0, windowStart: 0 },
};

function init() {
  if (state.initialized || !isBrowser()) return;

  state.initialized = true;
  state.bot = storage.get(STORAGE_KEYS.IS_BOT) === '1';

  state.taps = {
    count: Number(storage.get(STORAGE_KEYS.TAP_COUNT) ?? '0'),
    windowStart: Number(
      storage.get(STORAGE_KEYS.TAP_WINDOW_START) ?? Date.now(),
    ),
  };

  state.events = {
    count: Number(storage.get(STORAGE_KEYS.EVENT_COUNT) ?? '0'),
    windowStart: Number(
      storage.get(STORAGE_KEYS.EVENT_WINDOW_START) ?? Date.now(),
    ),
  };
}

function markAsBot(reason: string) {
  if (state.bot) return;

  // Recording started since this evt emitted
  posthog.capture('anomaly_detected', { reason });

  state.bot = true;
  storage.set(STORAGE_KEYS.IS_BOT, '1');
}

export function checkRepeatedPageviews(currentUrl: string) {
  init();
  if (state.bot) return;

  const pageviewHistory: string[] = JSON.parse(
    storage.get(STORAGE_KEYS.PAGEVIEWS) ?? '[]',
  );

  const updatedHistory = [...pageviewHistory.slice(-19), currentUrl];

  storage.set(STORAGE_KEYS.PAGEVIEWS, JSON.stringify(updatedHistory));

  const recentPageviews = updatedHistory.slice(-THRESHOLDS.REPEATED_PAGEVIEWS);

  const isAnomaly =
    recentPageviews.length >= THRESHOLDS.REPEATED_PAGEVIEWS &&
    recentPageviews.every((url) => url === recentPageviews[0]);

  if (isAnomaly) {
    markAsBot('repeated_pageview');
  }
}

export function checkEventRate() {
  init();
  if (state.bot) return;

  const now = Date.now();

  // Проверка за секунду
  const tapElapsed = now - state.taps.windowStart;

  if (tapElapsed >= 1000) {
    if (state.taps.count > THRESHOLDS.EVENTS_PER_SECOND) {
      markAsBot('autoclicker');
    }
    state.taps = { count: 1, windowStart: now };
  } else {
    state.taps = {
      ...state.taps,
      count: state.taps.count + 1,
    };
  }

  // Проверка за минуту
  const eventElapsed = now - state.events.windowStart;

  if (eventElapsed >= 60_000) {
    if (state.events.count > THRESHOLDS.EVENTS_PER_MINUTE) {
      markAsBot('event_flood');
    }
    state.events = { count: 1, windowStart: now };
  } else {
    state.events = {
      ...state.events,
      count: state.events.count + 1,
    };
  }

  storage.set(STORAGE_KEYS.TAP_COUNT, String(state.taps.count));
  storage.set(STORAGE_KEYS.TAP_WINDOW_START, String(state.taps.windowStart));
  storage.set(STORAGE_KEYS.EVENT_COUNT, String(state.events.count));
  storage.set(
    STORAGE_KEYS.EVENT_WINDOW_START,
    String(state.events.windowStart),
  );
}

export function isBotSession(): boolean {
  init();
  return state.bot;
}
